import { Link } from '@tanstack/react-router';
import { AlertTriangle, Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { ProductSchedule } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { useScheduleFormat } from '../hooks/use-schedule-format';

import { cadenceSummary } from './cadence-text';

function Fact({ label, children, tour }: { label: string; children: ReactNode; tour?: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-md border p-3" data-tour={tour}>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

/** Warnings the schedule raises, worst first. */
function Warnings({ schedule }: { schedule: ProductSchedule }) {
  const { t } = useTranslation('schedules');
  const notes: Array<{ id: string; tone: 'warning' | 'info'; title: string; body: string }> = [];
  if (schedule.pausedReason === 'run_failed') {
    notes.push({
      id: 'run-failed',
      tone: 'warning',
      title: t('warnings.runFailedTitle'),
      body: t('warnings.runFailed'),
    });
  }
  if (schedule.rulesTruncated) {
    notes.push({
      id: 'truncated',
      tone: 'warning',
      title: t('warnings.rulesTruncatedTitle'),
      body: t('warnings.rulesTruncated'),
    });
  }
  if (schedule.status !== 'ended' && schedule.stores.length === 0) {
    notes.push({
      id: 'no-stores',
      tone: 'warning',
      title: t('warnings.noStoresTitle'),
      body: t('warnings.noStores'),
    });
  }
  if (schedule.runningRunId) {
    notes.push({
      id: 'running',
      tone: 'info',
      title: t('warnings.runningTitle'),
      body: t('warnings.running'),
    });
  }
  if (schedule.pausedReason === 'manual') {
    notes.push({
      id: 'paused',
      tone: 'info',
      title: t('warnings.pausedTitle'),
      body: t('warnings.paused'),
    });
  }
  if (schedule.status === 'draft') {
    notes.push({
      id: 'draft',
      tone: 'info',
      title: t('warnings.draftTitle'),
      body: t('warnings.draft'),
    });
  }
  if (schedule.status === 'ended') {
    notes.push({
      id: 'ended',
      tone: 'info',
      title: t('warnings.endedTitle'),
      body: t('warnings.ended'),
    });
  }
  if (notes.length === 0) return null;
  return (
    <div className="flex flex-col gap-3" data-tour="schedules.detail.warnings">
      {notes.map((note) => (
        <Alert
          key={note.id}
          role={note.tone === 'warning' ? 'alert' : 'status'}
          className={note.tone === 'warning' ? 'border-warning/50' : undefined}
        >
          {note.tone === 'warning' ? <AlertTriangle aria-hidden /> : <Info aria-hidden />}
          <AlertTitle>{note.title}</AlertTitle>
          <AlertDescription>{note.body}</AlertDescription>
        </Alert>
      ))}
    </div>
  );
}

/** Where the schedule stands: warnings, next and last run, cadence, pool and each store's counts. */
export function ScheduleOverview({ schedule }: { schedule: ProductSchedule }) {
  const { t } = useTranslation('schedules');
  const format = useFormat();
  const zoned = useScheduleFormat(schedule.timeZone);

  return (
    <div className="flex flex-col gap-6">
      <Warnings schedule={schedule} />
      <FormSection title={t('overview.title')} tour="schedules.detail.summary">
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Fact label={t('overview.nextRun')} tour="schedules.detail.next-run">
            {schedule.nextRunAt ? (
              <span title={zoned.utc(schedule.nextRunAt)}>
                {zoned.dateTime(schedule.nextRunAt)}
                <span className="text-muted-foreground block text-xs font-normal">
                  {format.relative(schedule.nextRunAt)} · {schedule.timeZone}
                </span>
              </span>
            ) : schedule.firstRunAt ? (
              <span title={zoned.utc(schedule.firstRunAt)}>
                {t('overview.firstRun', { when: zoned.dateTime(schedule.firstRunAt) })}
                <span className="text-muted-foreground block text-xs font-normal">
                  {t('overview.firstRunHint', { runAt: schedule.runAt })} · {schedule.timeZone}
                </span>
              </span>
            ) : (
              <span className="text-muted-foreground">
                {t(`overview.noNextRun.${schedule.status}`, {
                  runAt: schedule.runAt,
                  start: zoned.dateTime(schedule.startsAt),
                })}
              </span>
            )}
          </Fact>
          <Fact label={t('overview.lastRun')}>
            {schedule.lastRunAt ? (
              <span title={zoned.utc(schedule.lastRunAt)}>
                {zoned.dateTime(schedule.lastRunAt)}
                <span className="text-muted-foreground block text-xs font-normal">
                  {format.relative(schedule.lastRunAt)}
                </span>
              </span>
            ) : (
              <span className="text-muted-foreground">{t('columns.never')}</span>
            )}
          </Fact>
          <Fact label={t('overview.cycles')}>
            {schedule.maxCycles === null
              ? t('overview.cyclesOpen', { count: schedule.cycleCount })
              : t('overview.cyclesOf', { count: schedule.cycleCount, max: schedule.maxCycles })}
          </Fact>
          <Fact label={t('overview.cadence')}>
            {cadenceSummary(t, schedule)}
            <span className="text-muted-foreground block text-xs font-normal">
              {schedule.timeZone}
            </span>
          </Fact>
          <Fact label={t('overview.pool')}>
            {t('columns.poolCount', { count: schedule.members.total })}
            <span className="text-muted-foreground block text-xs font-normal">
              {t('overview.poolDetail', {
                pinned: schedule.members.pinned,
                excluded: schedule.members.excluded,
              })}
            </span>
          </Fact>
          <Fact label={t('overview.ends')}>
            {schedule.endsAt ? (
              <span title={zoned.utc(schedule.endsAt)}>{zoned.dateTime(schedule.endsAt)}</span>
            ) : schedule.loop ? (
              t('overview.endsNever')
            ) : (
              t('overview.endsAfterAll')
            )}
            <span className="text-muted-foreground block text-xs font-normal">
              {t(`onEnd.${schedule.onEnd}`)}
            </span>
          </Fact>
        </dl>
      </FormSection>
      <FormSection
        title={t('overview.stores')}
        description={t('overview.storesDescription')}
        tour="schedules.detail.stores"
      >
        {schedule.stores.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('columns.noStores')}</p>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('overview.columns.store')}</TableHead>
                  <TableHead>{t('overview.columns.redirect')}</TableHead>
                  <TableHead className="text-right">{t('placement.scheduled')}</TableHead>
                  <TableHead className="text-right">{t('placement.live')}</TableHead>
                  <TableHead className="text-right">{t('placement.offline')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedule.stores.map((store) => (
                  <TableRow key={store.storeId}>
                    <TableCell>
                      <Link
                        to="/s/$storeId/catalog"
                        params={{ storeId: store.storeId }}
                        className="font-medium hover:underline"
                      >
                        {store.storeName}
                      </Link>
                      <span className="text-muted-foreground block text-xs">
                        {store.storeTimeZone}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {store.redirectCollection?.title ?? t('stores.noRedirect')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {store.placements.scheduled}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {store.placements.live}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {store.placements.offline}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-xs">
          <li>{t('explain.drafts')}</li>
          <li>{t('explain.manualEdits')}</li>
        </ul>
      </FormSection>
    </div>
  );
}
